import { Button } from '@/components/ui/button';
import {
  ComboboxPopover,
  type Option,
} from '@/components/ui/combobox/combobox-popover';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PDKEmptyValueIcon } from '@/components/ui/pdk-icons';
import { useProtectionPlanMutations } from '@/hooks/use-protection-plan-mutations';
import { toast } from '@/hooks/use-toast';
import { centsToInitialValue, currencyInputValueToCents } from '@/lib/currency';
import { cn } from '@/lib/utils';
import { protectionPlanNameStore } from '@/models/stores/protection-plan-names';
import { protectionPlanTermLengthStore } from '@/models/stores/protection-plan-term-lengths';
import { protectionPlanTermMilesStore } from '@/models/stores/protection-plan-term-miles';
import { protectionPlanTypeStore } from '@/models/stores/protection-plan-types';
import type { Deal, ProtectionPlan } from '@/models/types';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

type ProtectionPlanEditorDialogProps = {
  dealId: Deal['id'];
  protectionPlan: ProtectionPlan | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const ProtectionPlanEditorDialog = observer(
  function ProtectionPlanEditorDialog({
    dealId,
    protectionPlan,
    open,
    onOpenChange,
  }: ProtectionPlanEditorDialogProps) {
    const { upsertProtectionPlanMutation } = useProtectionPlanMutations({
      dealId,
      onSuccess: () => {
        toast({
          title: 'Protection plan updated',
          description: 'The protection plan has been successfully updated.',
        });
        onOpenChange(false);
      },
      onError: (error) => {
        toast({
          title: 'Error updating protection plan',
          description: error.message || 'Failed to update protection plan',
          variant: 'destructive',
        });
      },
    });

    // Initialize state from existing protection plan or empty values
    const [name, setName] = useState(protectionPlan?.name || '');
    const [type, setType] = useState(protectionPlan?.type || '');
    const [termLength, setTermLength] = useState<number | ''>(
      protectionPlan?.term_length || '',
    );
    const [termMiles, setTermMiles] = useState<number | ''>(
      protectionPlan?.term_miles || '',
    );
    const [monthlyPrice, setMonthlyPrice] = useState(
      protectionPlan?.monthly_price
        ? centsToInitialValue(protectionPlan.monthly_price)
        : '',
    );
    const [totalPrice, setTotalPrice] = useState(
      protectionPlan?.price ? centsToInitialValue(protectionPlan.price) : '',
    );

    const wrappedOnOpenChange = useCallback(
      (newOpenValue: boolean) => {
        onOpenChange(newOpenValue); // Pass through the actual value
        if (newOpenValue === false) {
          // reset everything when the dialog is closed
          setName(protectionPlan?.name || '');
          setType(protectionPlan?.type || '');
          setTermLength(protectionPlan?.term_length || '');
          setTermMiles(protectionPlan?.term_miles || '');
          setMonthlyPrice(
            protectionPlan?.monthly_price
              ? centsToInitialValue(protectionPlan.monthly_price)
              : '',
          );
          setTotalPrice(
            protectionPlan?.price
              ? centsToInitialValue(protectionPlan.price)
              : '',
          );
          // Close all popovers
          setNameOpen(false);
          setTypeOpen(false);
          setTermLengthOpen(false);
          setTermMilesOpen(false);
        }
      },
      [onOpenChange],
    );

    // Add state for popover open/close
    const [nameOpen, setNameOpen] = useState(false);
    const [typeOpen, setTypeOpen] = useState(false);
    const [termLengthOpen, setTermLengthOpen] = useState(false);
    const [termMilesOpen, setTermMilesOpen] = useState(false);

    // Close all popovers when dialog closes
    useEffect(() => {
      if (!open) {
        setNameOpen(false);
        setTypeOpen(false);
        setTermLengthOpen(false);
        setTermMilesOpen(false);
      }
    }, [open]);

    // Create option arrays for comboboxes using hydrated models
    const nameOptions: Option<string>[] = useMemo(() => {
      return protectionPlanNameStore.all.map((n) => ({
        value: n.name,
        label: n.name,
      }));
    }, [protectionPlanNameStore.all]);

    const typeOptions: Option<string>[] = useMemo(() => {
      return protectionPlanTypeStore.all.map((t) => ({
        value: t.type,
        label: t.type,
      }));
    }, [protectionPlanTypeStore.all]);

    const termLengthOptions: Option<string>[] = useMemo(() => {
      return protectionPlanTermLengthStore.all.map((tl) => ({
        value: String(tl.term_length),
        label: `${tl.term_length} months`,
      }));
    }, [protectionPlanTermLengthStore.all]);

    const termMilesOptions: Option<string>[] = useMemo(() => {
      return protectionPlanTermMilesStore.all.map((tm) => ({
        value: String(tm.term_miles),
        label: `${tm.term_miles.toLocaleString()} miles`,
      }));
    }, [protectionPlanTermMilesStore.all]);

    const handleSubmit = () => {
      // Validation
      if (!name) {
        toast({
          title: 'Name is required',
          description: 'Please select a protection plan name',
          variant: 'destructive',
        });
        return;
      }
      if (!type) {
        toast({
          title: 'Type is required',
          description: 'Please select a protection plan type',
          variant: 'destructive',
        });
        return;
      }
      if (!termLength || typeof termLength !== 'number') {
        toast({
          title: 'Term length is required',
          description: 'Please select a term length',
          variant: 'destructive',
        });
        return;
      }
      if (!termMiles || typeof termMiles !== 'number') {
        toast({
          title: 'Term miles is required',
          description: 'Please select term miles',
          variant: 'destructive',
        });
        return;
      }
      if (!monthlyPrice) {
        toast({
          title: 'Monthly price is required',
          description: 'Please enter a monthly price',
          variant: 'destructive',
        });
        return;
      }
      if (!totalPrice) {
        toast({
          title: 'Total price is required',
          description: 'Please enter a total price',
          variant: 'destructive',
        });
        return;
      }

      const monthlyPriceCents = currencyInputValueToCents(monthlyPrice);
      const totalPriceCents = currencyInputValueToCents(totalPrice);

      if (monthlyPriceCents <= 0) {
        toast({
          title: 'Invalid monthly price',
          description: 'Please enter a positive monthly price',
          variant: 'destructive',
        });
        return;
      }
      if (totalPriceCents <= 0) {
        toast({
          title: 'Invalid total price',
          description: 'Please enter a positive total price',
          variant: 'destructive',
        });
        return;
      }

      upsertProtectionPlanMutation.mutate({
        protectionPlan: {
          id: protectionPlan?.id || uuidv4(),
          name,
          type,
          term_length: termLength,
          term_miles: termMiles,
          monthly_price: monthlyPriceCents,
          price: totalPriceCents,
        },
      });
    };

    const handleNameChange = (value: string) => {
      setName(value);
    };

    const handleTypeChange = (value: string) => {
      setType(value);
    };

    const handleTermLengthChange = (value: string) => {
      setTermLength(value ? Number(value) : '');
    };

    const handleTermMilesChange = (value: string) => {
      setTermMiles(value ? Number(value) : '');
    };

    const currentNameOption = useMemo(
      () => nameOptions.find((opt) => opt.value === name),
      [nameOptions, name],
    );

    const currentTypeOption = useMemo(
      () => typeOptions.find((opt) => opt.value === type),
      [typeOptions, type],
    );

    const currentTermLengthOption = useMemo(
      () => termLengthOptions.find((opt) => opt.value === String(termLength)),
      [termLengthOptions, termLength],
    );

    const currentTermMilesOption = useMemo(
      () => termMilesOptions.find((opt) => opt.value === String(termMiles)),
      [termMilesOptions, termMiles],
    );

    return (
      <Dialog open={open} onOpenChange={wrappedOnOpenChange}>
        <DialogContent
          className="sm:max-w-[500px]"
          onClickClose={() => wrappedOnOpenChange(false)}
        >
          <DialogHeader>
            <DialogTitle>
              {protectionPlan ? 'Edit Protection Plan' : 'Add Protection Plan'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <div className="flex flex-row gap-2 items-center justify-between">
              <label className="text-sm">Name</label>
              <ComboboxPopover
                open={nameOpen}
                onOpenChange={setNameOpen}
                endAdornment={null}
                placeholder="Set name..."
                emptyMessage="No names found"
                currentOption={currentNameOption}
                options={nameOptions}
                onChange={handleNameChange}
                align="start"
                stayOpenAfterSelection={false}
              >
                <div>
                  <Button
                    variant="ghost"
                    className={cn('p-1 h-fit', {
                      'text-gray-600': !name,
                    })}
                  >
                    {!name ? (
                      <>
                        <PDKEmptyValueIcon className="h-3.5 w-3.5" />
                        <p className="rounded text-xs text-gray-600">
                          Select name...
                        </p>
                      </>
                    ) : (
                      <p className="rounded text-xs">{name}</p>
                    )}
                  </Button>
                </div>
              </ComboboxPopover>
            </div>

            <div className="flex flex-row gap-2 items-center justify-between">
              <label className="text-sm">Type</label>
              <ComboboxPopover
                open={typeOpen}
                onOpenChange={setTypeOpen}
                endAdornment={null}
                placeholder="Set type..."
                emptyMessage="No types found"
                currentOption={currentTypeOption}
                options={typeOptions}
                onChange={handleTypeChange}
                align="start"
                stayOpenAfterSelection={false}
              >
                <div>
                  <Button
                    variant="ghost"
                    className={cn('p-1 h-fit', {
                      'text-gray-600': !type,
                    })}
                  >
                    {!type ? (
                      <>
                        <PDKEmptyValueIcon className="h-3.5 w-3.5" />
                        <p className="rounded text-xs text-gray-600">
                          Select type...
                        </p>
                      </>
                    ) : (
                      <p className="rounded text-xs">{type}</p>
                    )}
                  </Button>
                </div>
              </ComboboxPopover>
            </div>

            <div className="flex flex-row gap-2 items-center justify-between">
              <label className="text-sm">Term Length</label>
              <ComboboxPopover
                open={termLengthOpen}
                onOpenChange={setTermLengthOpen}
                endAdornment={null}
                placeholder="Set term length..."
                emptyMessage="No term lengths found"
                currentOption={currentTermLengthOption}
                options={termLengthOptions}
                onChange={handleTermLengthChange}
                align="start"
                stayOpenAfterSelection={false}
              >
                <div>
                  <Button
                    variant="ghost"
                    className={cn('p-1 h-fit', {
                      'text-gray-600': !termLength,
                    })}
                  >
                    {!termLength ? (
                      <>
                        <PDKEmptyValueIcon className="h-3.5 w-3.5" />
                        <p className="rounded text-xs text-gray-600">
                          Select term length...
                        </p>
                      </>
                    ) : (
                      <p className="rounded text-xs">
                        {currentTermLengthOption?.label || termLength}
                      </p>
                    )}
                  </Button>
                </div>
              </ComboboxPopover>
            </div>

            <div className="flex flex-row gap-2 items-center justify-between">
              <label className="text-sm">Term Miles</label>
              <ComboboxPopover
                open={termMilesOpen}
                onOpenChange={setTermMilesOpen}
                endAdornment={null}
                placeholder="Set term miles..."
                emptyMessage="No term miles found"
                currentOption={currentTermMilesOption}
                options={termMilesOptions}
                onChange={handleTermMilesChange}
                align="start"
                stayOpenAfterSelection={false}
              >
                <div>
                  <Button
                    variant="ghost"
                    className={cn('p-1 h-fit', {
                      'text-gray-600': !termMiles,
                    })}
                  >
                    {!termMiles ? (
                      <>
                        <PDKEmptyValueIcon className="h-3.5 w-3.5" />
                        <p className="rounded text-xs text-gray-600">
                          Select term miles...
                        </p>
                      </>
                    ) : (
                      <p className="rounded text-xs">
                        {currentTermMilesOption?.label || termMiles}
                      </p>
                    )}
                  </Button>
                </div>
              </ComboboxPopover>
            </div>

            <div className="flex flex-row gap-2 items-center justify-between">
              <label className="text-sm">Monthly Price</label>
              <CurrencyInput
                className="w-full text-right"
                placeholder="0.00"
                value={monthlyPrice}
                onChange={(e) => setMonthlyPrice(e.target.value)}
              />
            </div>

            <div className="flex flex-row gap-2 items-center justify-between">
              <label className="text-sm">Total Price</label>
              <CurrencyInput
                className="w-full text-right"
                placeholder="0.00"
                value={totalPrice}
                onChange={(e) => setTotalPrice(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => wrappedOnOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={upsertProtectionPlanMutation.isPending}
            >
              {protectionPlan ? 'Update' : 'Add'} Protection Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);
